import { useEffect, useState } from "react";
import { Bell, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useT } from "../lib/LangContext";

// LocalStorage key marking that the user has *explicitly* opted in via
// the site UI (as opposed to just having granted permission from a
// previous session). We use this to know whether to show the button.
const OPTED_IN_KEY = "push_opted_in";
const DISMISSED_KEY = "push_dismissed";

const urlBase64ToUint8Array = (base64) => {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const PushOptIn = ({ islandSlug = "all" }) => {
  const { t, lang } = useT();
  const [state, setState] = useState("idle"); // idle | subscribed | denied | dismissed | unsupported
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) {
      setState("unsupported");
      return;
    }
    try {
      if (sessionStorage.getItem(DISMISSED_KEY) === "1") {
        setState("dismissed");
        return;
      }
    } catch (_) { /* private mode */ }
    if (Notification.permission === "denied") {
      setState("denied");
      return;
    }
    if (Notification.permission === "granted" &&
        localStorage.getItem(OPTED_IN_KEY) === "1") {
      setState("subscribed");
    }
  }, []);

  const subscribe = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Register (or re-use) our service worker
      const reg = await navigator.serviceWorker.register("/sw.js");
      // Fetch the VAPID public key from the backend
      const keyRes = await api.get("/push/vapid-key");
      if (!keyRes.data.configured) {
        toast.error("Push notifications not configured yet.");
        return;
      }
      const applicationServerKey = urlBase64ToUint8Array(keyRes.data.public_key);
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState(permission === "denied" ? "denied" : "idle");
        return;
      }
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      await api.post("/push/subscribe", {
        subscription: subscription.toJSON(),
        lang,
        island: islandSlug,
      });
      try { localStorage.setItem(OPTED_IN_KEY, "1"); } catch (_) { /* ignore */ }
      setState("subscribed");
      toast.success("You'll get a daily headline ping.");
    } catch (e) {
      toast.error("Couldn't enable notifications.");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISSED_KEY, "1"); } catch (_) { /* ignore */ }
    setState("dismissed");
  };

  if (state === "unsupported" || state === "denied" || state === "dismissed") {
    return null;
  }

  if (state === "subscribed") {
    return (
      <div
        className="card-soft p-4 flex items-center gap-3 text-[13px] text-[#0AADA0]"
        data-testid="push-optin-subscribed"
      >
        <Check size={16} className="shrink-0" />
        <span>{t("push_subscribed_label")}</span>
      </div>
    );
  }

  return (
    <div className="card-soft p-4 relative" data-testid="push-optin">
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 text-[#6B7280] hover:text-[#1A1A1A]"
        data-testid="push-optin-dismiss"
      >
        <X size={13} />
      </button>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">
        <Bell size={12} />
        <span>{t("push_title")}</span>
      </div>
      <p className="text-[13px] text-[#1A2E35] leading-snug mb-3">
        {t("push_desc")}
      </p>
      <button
        type="button"
        onClick={subscribe}
        disabled={busy}
        className="w-full py-2 rounded-full bg-[#0AADA0] hover:bg-[#08988c] text-white text-[13px] font-medium disabled:opacity-60 transition-colors"
        data-testid="push-optin-btn"
      >
        {busy ? "…" : t("push_enable")}
      </button>
    </div>
  );
};
